<template>
  <div>
    <div class="row">
      <h6 class="heading-small text-muted mb-4 pl-2">
        <i class="fas fa-tachometer-alt"></i> {{ title }}
      </h6>
    </div>
    <div class="row vld-parent" ref="monthly_stats">
      <div
        class="col-lg-6 col-md-6 mb-3 d-flex align-items-stretch"
        v-for="(metric, index) in notHiddenMetrics"
        :key="'metric_' + metric.short_name + '_' + index"
      >
        <stats-card
          :title="metric.name"
          :iconInTop="true"
          :type="metric.chart ? 'gradient-info' : 'gradient-info'"
          :sub-title="metric.formattedValue"
          :icon="metric.chart ? 'fas fa-chart-line' : 'fas fa-tachometer-alt'"
          class="btn mb-4 mb-xl-0 flex-grow-1 cursor-pointer pb-0 pt-1"
          @click.native="handleOpenModalInfo(metric)"
          :margin="true"
        >
        </stats-card>
      </div>
      <div class="col-lg-6 col-md-6 mb-3 d-flex align-items-stretch">
        <stats-card
          title="Última atualização"
          :iconInTop="true"
          type="gray"
          :sub-title="getDate"
          icon="fas fa-calendar-alt"
          class="text-center mb-4 mb-xl-0 flex-grow-1 pb-0 pt-1"
          :margin="true"
        >
        </stats-card>
      </div>
    </div>
    <modal
      :show.sync="modals.show"
      :modal-classes="{
        'modal-lg': modals.chartData,
        'modal-dialog-centered': true,
      }"
      headerClasses="pb-1"
    >
      <h6 slot="header" class="modal-title">Acompanhamento</h6>
      <div class="text-center">
        <i class="fas fa-tachometer-alt text-warning fa-3x"></i>
        <h4 class="heading mt-4">
          {{ modals.metric.name }} ({{
            modals.metric.custom_name || modals.metric.short_name
          }})
        </h4>
        <div class="col-12 text-sm mt-3">
          <i class="fas fa-quote-left mr-1"></i>{{ modals.metric.description
          }}<i class="fas fa-quote-right ml-1"></i>
        </div>
        <div class="col-12 mt-4">
          <startup-stat-chart
            :rawData="modals.chartData"
            :title="modals.metric.custom_name || modals.metric.short_name"
            :subtitle="modals.metric.name"
            v-if="modals.chartData"
          />
        </div>
      </div>
      <template slot="footer">
        <base-button type="link" class="ml-auto" @click="modals.show = false">
          Fechar
        </base-button>
      </template>
    </modal>
  </div>
</template>
<script>
import api from "@/services/api";
import StartupStatChart from "./StartupStatChart";

export default {
  name: "startup-metrics",
  components: {
    StartupStatChart,
  },
  props: {
    batchId: {
      type: Number,
      description: "Batch identifier to fetch data (for startup's dashboard)",
    },
    data: {
      type: Array,
      default: () => [],
      description: "Pré-fetched data (for investor's dashboard)",
    },
    title: {
      type: String,
      default: "Indicadores do último mês",
      description: "Upper title",
    },
  },
  data() {
    return {
      metrics: {
        mrr: {
          short_name: "MRR",
          name: "Monthly Recurring Revenue",
          formattedValue: "-",
          value: null,
          chart: true,
          description:
            "O Monthly Recurring Revenue (Receita Recorrente Mensal) é um idicador que mostra a sustentabilidade financeira da startup. Essa métrica é utilizada para calcular os valores que entram mês a mês e, inclusive, prever ganhos futuros. Conhecer o MMR é crucial para startups SaaS, pois ele une as áreas financeiras e estratégicas em torno do mesmo potencial de crescimento.",
        },
        growthMrr: {
          short_name: "Taxa de Crescimento do MRR",
          name: "MRR Growth Rate",
          formattedValue: "-",
          value: null,
          description:
            "O Monthly Recurring Revenue (Receita Recorrente Mensal) é um idicador que mostra a sustentabilidade financeira da startup. Essa métrica é utilizada para calcular os valores que entram mês a mês e, inclusive, prever ganhos futuros. Conhecer o MMR é crucial para startups SaaS, pois ele une as áreas financeiras e estratégicas em torno do mesmo potencial de crescimento. O MRR Growth Rate (Taxa de Crescimento do MRR) representa o crescimento do MRR considerando os dois últimos valores observados.",
        },
        cr: {
          short_name: "CR",
          name: "Churn Rate",
          formattedValue: "-",
          value: null,
          chart: true,
          description:
            "O Churn Rate (Taxa de Rotatividade de Clientes) mede o número de clientes ou assinantes perdidos em um determinado período. Tal compreensão é essencial para o planejamento do negócio a curto e a médio prazo da startup, uma vez que para expandir sua base de clientes é preciso que o número de novos clientes exceda o seu CR, i.e., clientes cancelados.",
        },
        ltv: {
          short_name: "LTV",
          name: "Lifetime Value",
          formattedValue: "-",
          value: null,
          description:
            "O LifeTime Value (Valor do Tempo de Vida do Cliente) é uma métrica de negócios que estima o lucro líquido da vida de cada cliente, ou seja, inidica o valor de um cliente para a startup durante todo seu relacionamento com ela. Com o cálculo do LTV, é possível elaborar um orçamento mais inteligente e atrelado à real situação da startup.",
        },
        cac: {
          short_name: "CAC",
          name: "Customer Acquisiton Cost",
          formattedValue: "-",
          value: null,
          chart: true,
          description:
            "O Customer Acquisition Cost (Custo de Aquisição de Clientes) é um indicador que mede a quantia de dinheiro que a startup precisa investir para convencer o consumidor a realizar uma compra. O CAC é essencial para a análise de viabilidade de um determinado modelo de negócios, auxiliando a determinar, com exatidão, o teto de gastos que pode ser atingido na busca por novos clientes.",
        },
        ltvCac: {
          short_name: "Lifetime Value / Customer Acquisiton Cost",
          name: "LTV/CAC",
          formattedValue: "-",
          value: null,
          description:
            'O LTV/CAC trata-se de uma das mais importantes métricas para empresas de SaaS, gerando insights de saúde financeira e de atratividade de investimentos. A referência mais utilizada pelos investidores de capital do risco nos EUA é maior ou igual a 3, ou seja, o LTV total do cliente é pelo menos 3x maior que o custo para adquiri-lo. Portanto, "LTV ≥ 3x means scale"!',
        },
        arpu: {
          short_name: "ARPU",
          name: "Average Revenue per User",
          formattedValue: "-",
          value: null,
          description:
            "O Average Revenue Per User (Receita Média por Usuário) mede quanto cada cliente gera de receita para o negócio em um determinado período de tempo. A compreensão do valor gerado à empresa por cada cliente ativo permite à gestão calcular estrategicamente o investimento em questões como suporte e atendimento ao usuário. Assim, o ARPU permite entender de forma otimizada as preferências do consumidor. ",
        },
        cacPayback: {
          short_name: "Tempo de Retorno do CAC",
          name: "CAC Payback Period",
          formattedValue: "-",
          value: null,
          description:
            "O CAC Payback (Tempo de Retorno do CAC) mede quanto tempo leva para um cliente gerar receita líquida suficiente para cobrir o CAC, ou seja, o número de meses necessários para recuperar o dinheiro investido na aquisição de clientes. Este KPI (Key Performance Indicator) tem um impacto direto no fluxo de caixa e gera insights sobre o breakeven (ponto de equilíbrio) da startup.",
        },
        lvr: {
          short_name: "LVR",
          name: "Lead Velocity Rate",
          formattedValue: "-",
          value: null,
          description:
            "O indicador Lead Velocity Rate (Taxa de Crescimento de Leads) quantifica o crescimento do negócio em termos de leads qualificados. Se medido mensalmente, o LVR pode gerar insights precisos sobre o crescimento do negócio em termos de receita e aquisição de clientes. Trata-se, portanto, de um mecanismo eficiente para um acompanhamento em tempo real da performance do time de vendas da startup.",
        },
        lcr: {
          short_name: "LCR",
          name: "Lead Conversion Rate",
          formattedValue: "-",
          value: null,
          description:
            "Tão importante quanto o volume de leads gerados é a eficiência em sua conversão. O Lead Conversion Rate (Taxa de Conversão de Leads) é o indicador que mede quanto da audiência da startup realmente converte e gera resultados para o negócio. Para que o LCR seja utilizado com excelência, é fundamental que a gestão entenda os fatores internos e externos da startup. Dessa forma, é possível determinar, sem achismos, se a taxa é satisfatória ou se é preciso tomar ações para sua otimização.",
        },
        monthGbr: {
          short_name: "GBR do mês",
          name: "Month Gross Burn Rate",
          formattedValue: "-",
          value: null,
          description:
            "Burn Rate (Taxa de Queima) representa a velocidade com a qual uma startup gasta o seu capital para alcançar o break-even (ponto de equilíbrio) ou mesmo se tornar rentável. A queima bruta (Gross Burn) demonstra o valor total de custos operacionais incorridos em despesas a cada mês. O GBR fornece insights sobre os geradores de custo (cost drivers) e a eficiência de uma startup, independentemente de sua receita. O Month GBR considera os valores do mês atual para o cálculo da métrica",
        },
        gbr: {
          short_name: "GBR médio",
          name: "Average Gross Burn Rate",
          formattedValue: "-",
          value: null,
          description:
            "Burn Rate (Taxa de Queima) representa a velocidade com a qual uma startup gasta o seu capital para alcançar o break-even (ponto de equilíbrio) ou mesmo se tornar rentável. A queima bruta (Gross Burn) demonstra o valor total de custos operacionais incorridos em despesas a cada mês. O GBR fornece insights sobre os geradores de custo (cost drivers) e a eficiência de uma startup, independentemente de sua receita. O Average GBR considera a média do histórico de valores observados para o cálculo da métrica.",
        },
        monthNbr: {
          short_name: "NBR do mês",
          name: "Month Net Burn Rate",
          formattedValue: "-",
          value: null,
          description:
            "Burn Rate (Taxa de Queima) representa a velocidade com a qual uma startup gasta o seu capital para alcançar o break-even (ponto de equilíbrio) ou mesmo se tornar rentável. A queima líquida (Net Burn) demonstra o quão rápido uma startup está usando seu capital, levando em consideração a receita gerada. Com isso, o NBR permite avaliar quanto dinheiro uma startup precisa para continuar operando por um determinado período de tempo. O Month NBR considera os valores do mês atual para o cálculo da métrica.",
        },
        nbr: {
          short_name: "NBR médio",
          name: "Average Net Burn Rate",
          formattedValue: "-",
          value: null,
          description:
            "Burn Rate (Taxa de Queima) representa a velocidade com a qual uma startup gasta o seu capital para alcançar o break-even (ponto de equilíbrio) ou mesmo se tornar rentável. A queima líquida (Net Burn) demonstra o quão rápido uma startup está usando seu capital, levando em consideração a receita gerada. Com isso, o NBR permite avaliar quanto dinheiro uma startup precisa para continuar operando por um determinado período de tempo. O Average NBR considera a média do histórico de valores observados para o cálculo da métrica.",
        },
        rw: {
          short_name: "RW",
          name: "Cash Runway",
          formattedValue: "-",
          value: null,
          description:
            " O RunWay (Tamanho da pista de decolagem) expressa quanto tempo uma startup terá para conseguir decolar e emplacar no mercado. O RW é um Key Performance Indicator (Indicador-chave de desempenho) que representa quanto tempo a startup sobrevive sem nenhuma rodada adicional de investimento, assumindo que a entrada de caixa e despesas continuem constantes.",
        },
        upsell: {
          short_name: "Upsell",
          custom_name: "Up-selling",
          name: "Total de vendas com up-sell",
          formattedValue: "-",
          value: null,
          chart: true,
          description:
            "Up selling é uma estratégia de vendas que, diferente da abordagem do cross selling, envolve incentivar os clientes a adquirir uma versão mais sofisticada ou avançada do produto que originalmente pretendiam comprar.",
        },
      },
      startupResponsesHistory: {
        initial_clients: [],
        initial_leads: [],
        initial_balance: [],
        new_clients: [],
        lost_clients: [],
        new_leads: [],
        recommendation_leads: [],
        mrr: [],
        upsell: [],
        setup_revenue: [],
        other_revenue: [],
        marketing_expenses: [],
        selling_expenses: [],
        other_expenses: [],
      },
      startupCurrentMonthReport: {
        new_clients: "",
        lost_clients: "",
        new_leads: "",
        recommendation_leads: "",
        mrr: "",
        upsell: "",
        setup_revenue: "",
        other_revenue: "",
        marketing_expenses: "",
        selling_expenses: "",
        other_expenses: "",
      },
      charts: {
        mrr: [],
        upsell: [],
        cac: [],
        cr: [],
      },
      modals: {
        show: false,
        metric: { name: "-", short_name: "-", chartData: false },
      },
    };
  },
  async mounted() {
    const loaderMonthlyStats = this.$loading.show({
      container: this.$refs.monthly_stats,
      width: 140,
      height: 140,
    });
    try {
      let data = this.data;
      if (this.batchId) {
        const { data: responses } = await api.get(
          `/startup-dashboard/${this.batchId}`
        );
        data = responses;
      }
      if (data.length) this.prepareData(data);
      loaderMonthlyStats.hide();
    } catch (e) {
      this.$toasted.error("Ocorreu um erro ao calcular os indicadores");
    }
  },
  watch: {
    data() {
      const loaderMonthlyStats = this.$loading.show({
        container: this.$refs.monthly_stats,
        width: 140,
        height: 140,
      });
      this.prepareData(this.data);
      loaderMonthlyStats.hide();
    },
  },
  methods: {
    prepareData(data) {
      this.splitResponsesByFamily(data);
      this.findLastMonthResponses(this.startupResponsesHistory);
      this.calcMetrics(
        this.startupResponsesHistory,
        this.startupCurrentMonthReport
      );
      try {
        this.calcChartData(this.startupResponsesHistory);
      } catch (e) {
        this.$toasted.error(
          "Ocorreu um erro ao gerar os gráficos de desempenho"
        );
      }
    },
    splitResponsesByFamily(responses) {
      responses.forEach((response) => {
        if (response.family in this.startupResponsesHistory)
          this.startupResponsesHistory[response.family].push({
            value: response.answer_number,
            date: response.start_date,
            visible: response.visible === undefined || response.visible,
          });
      });
    },
    findLastMonthResponses(history) {
      const current = this.startupCurrentMonthReport;
      Object.keys(current).forEach((metric) => {
        current[metric] = this.getLastMonthAnswer(history[metric]);
      });
    },
    getLastMonthAnswer(array) {
      if (!array.length) return null;
      return array[array.length - 1].value;
    },
    sum(array) {
      return array.length ? array.reduce((a, b) => a + b.value, 0) : null;
    },
    calcMetrics(history, current) {
      const sum_new_clients = this.sum(history.new_clients);
      const sum_lost_clients = this.sum(history.lost_clients);
      const sum_expenses =
        this.sum(history.marketing_expenses) +
        this.sum(history.selling_expenses) +
        this.sum(history.other_expenses);
      const months_expenses = Math.max.apply(null, [
        history.marketing_expenses.length,
        history.selling_expenses.length,
        history.other_expenses.length,
      ]);
      const current_expenses =
        current.marketing_expenses +
        current.selling_expenses +
        current.other_expenses;
      const current_revenue =
        current.mrr + current.setup_revenue + current.other_revenue;
      const sum_revenues =
        this.sum(history.mrr) +
        this.sum(history.setup_revenue) +
        this.sum(history.other_revenue);
      const months_revenues = Math.max.apply(null, [
        history.mrr.length,
        history.setup_revenue.length,
        history.other_revenue.length,
      ]);
      this.calcMrr(current.mrr, history.mrr);
      this.calcUpsell(history.upsell);
      this.calcCac(
        current.marketing_expenses,
        current.selling_expenses,
        history.new_clients[history.new_clients.length - 1]
      );
      this.calcLvr(
        current.new_leads,
        history.initial_leads[0] ? history.initial_leads[0].value : null,
        history.new_leads
      );
      this.calcLcr(current.new_clients, current.new_leads);
      this.calcArpu(
        this.metrics.mrr.value,
        sum_lost_clients,
        sum_new_clients,
        history.initial_clients[0] ? history.initial_clients[0].value : null
      );
      this.calcCacPayback(this.metrics.cac.value, this.metrics.arpu.value);
      this.calcCr(
        current.lost_clients,
        sum_new_clients - current.new_clients, // inicio do mês
        sum_lost_clients - current.lost_clients,
        history.initial_clients[0] ? history.initial_clients[0].value : null
      );
      this.calcLtv(this.metrics.cr.value, this.metrics.arpu.value);
      this.calcLtvCac(this.metrics.ltv.value, this.metrics.cac.value);
      this.calcGbr(sum_expenses, months_expenses, current_expenses);
      this.calcNbr(
        this.metrics.gbr.value,
        sum_revenues,
        months_revenues,
        current_revenue,
        this.metrics.monthGbr.value
      );
      this.calcRw(
        history.initial_balance[0] ? history.initial_balance[0].value : null,
        sum_expenses,
        sum_revenues,
        this.metrics.nbr.value
      );
    },
    calcMrr(mrr, history_mrr) {
      if (mrr == null) return;
      this.metrics.mrr.value = mrr;
      this.metrics.mrr.formattedValue = this.formatValue(mrr);
      if (history_mrr.length > 1) {
        const last_month_mrr = history_mrr[history_mrr.length - 2].value;
        if (last_month_mrr > 0) {
          this.metrics.growthMrr.value =
            ((mrr - last_month_mrr) / last_month_mrr) * 100;
          this.metrics.growthMrr.formattedValue = this.getPercentageStr(
            this.metrics.growthMrr.value
          );
        }
      }
    },
    calcUpsell(history_upsell) {
      if (history_upsell == null || history_upsell.length < 1) return;
      this.metrics.upsell.value = this.sum(history_upsell);
      this.metrics.upsell.formattedValue = this.formatValue(
        this.metrics.upsell.value
      );
    },
    calcCac(marketing_expenses, selling_expenses, new_clients) {
      if (
        new_clients &&
        new_clients.value > 0 &&
        marketing_expenses !== null &&
        selling_expenses !== null
      ) {
        this.metrics.cac.value =
          (marketing_expenses + selling_expenses) / new_clients.value;
        if (new_clients.visible)
          this.metrics.cac.formattedValue = this.formatValue(
            this.metrics.cac.value
          );
      }
    },
    calcLvr(new_leads, initial_leads, history_new_leads) {
      if (history_new_leads.length <= 1) return;

      const leads_until_previous_month = this.sumResponsesUntilPreviousMonth(
        history_new_leads,
        history_new_leads[history_new_leads.length - 1].date
      );
      const total_leads = initial_leads + leads_until_previous_month;
      if (total_leads > 0 && new_leads !== null) {
        this.metrics.lvr.value = (new_leads / total_leads) * 100;
        this.metrics.lvr.formattedValue = this.getPercentageStr(
          this.metrics.lvr.value
        );
      }
    },
    calcLcr(new_clients, new_leads) {
      if (new_leads > 0 && new_clients !== null) {
        this.metrics.lcr.value = (new_clients / new_leads) * 100;
        this.metrics.lcr.formattedValue = this.getPercentageStr(
          this.metrics.lcr.value
        );
      }
    },
    calcArpu(mrr, sum_lost_clients, sum_new_clients, initial_clients) {
      const total_clients =
        initial_clients + sum_new_clients - sum_lost_clients;
      if (mrr !== null && total_clients > 0) {
        this.metrics.arpu.value = mrr / total_clients;
        this.metrics.arpu.formattedValue = this.formatValue(
          this.metrics.arpu.value
        );
      }
    },
    calcCacPayback(cac, arpu) {
      if (cac > 0 && arpu > 0) {
        this.metrics.cacPayback.value = Math.abs(
          Math.round((cac / arpu) * 100) / 100
        );
        this.metrics.cacPayback.formattedValue = `${this.metrics.cacPayback.value.toLocaleString(
          "pt-BR"
        )} ${this.metrics.rw.value === 1 ? "mês" : "meses"}`;
      }
    },
    calcCr(lost_clients, sum_new_clients, sum_lost_clients, initial_clients) {
      const total_clients =
        initial_clients + sum_new_clients - sum_lost_clients;
      if (lost_clients >= 0 && total_clients > 0) {
        this.metrics.cr.value = (lost_clients / total_clients) * 100;
        this.metrics.cr.formattedValue = this.getPercentageStr(
          this.metrics.cr.value
        );
      }
    },
    calcLtv(cr, arpu) {
      if (arpu > 0 && cr > 0) {
        this.metrics.ltv.value = (arpu / cr) * 100;
        this.metrics.ltv.formattedValue = this.formatValue(
          this.metrics.ltv.value
        );
      }
    },
    calcLtvCac(ltv, cac) {
      if (ltv != null && cac > 0) {
        this.metrics.ltvCac.value = ltv / cac;
        this.metrics.ltvCac.formattedValue =
          (Math.round(this.metrics.ltvCac.value * 100) / 100).toLocaleString(
            "pt-BR"
          ) + "x";
      }
    },
    calcGbr(sum_expenses, months, current_expenses) {
      if (sum_expenses !== null && months > 0) {
        this.metrics.gbr.value = sum_expenses / months;
        this.metrics.gbr.formattedValue = this.formatValue(
          this.metrics.gbr.value
        );
      }
      if (current_expenses > 0) {
        this.metrics.monthGbr.value = current_expenses;
        this.metrics.monthGbr.formattedValue = this.formatValue(
          this.metrics.monthGbr.value
        );
      }
    },
    calcNbr(gbr, sum_revenues, months, month_revenue, month_gbr) {
      if (sum_revenues !== null && gbr !== null && months > 0) {
        this.metrics.nbr.value = (gbr * months - sum_revenues) / months;
        if (this.metrics.nbr.value > 0)
          this.metrics.nbr.formattedValue = this.formatValue(
            this.metrics.nbr.value,
            true
          );
        else this.metrics.nbr.formattedValue = "≤ 0, ótimo!";
      }
      if (month_revenue !== null && month_gbr !== null) {
        this.metrics.monthNbr.value = month_gbr - month_revenue;
        if (this.metrics.monthNbr.value > 0)
          this.metrics.monthNbr.formattedValue = this.formatValue(
            this.metrics.monthNbr.value,
            true
          );
        else this.metrics.monthNbr.formattedValue = "≤ 0, ótimo!";
      }
    },
    calcRw(initial_balance, sum_expenses, sum_revenues, nbr) {
      if (
        nbr === null ||
        initial_balance === null ||
        sum_expenses === 0 ||
        sum_revenues === 0
      )
        return;
      const current_balance = initial_balance + sum_revenues - sum_expenses;
      if (nbr !== 0) {
        if (current_balance <= 0 && nbr > 0) {
          this.metrics.rw.formattedValue = "> 0";
          return;
        }
        if (nbr <= 0) {
          this.metrics.rw.formattedValue = "≤ 0, ótimo!";
          return;
        }
        this.metrics.rw.value = Math.abs(
          Math.round((current_balance / nbr) * 100) / 100
        );
        this.metrics.rw.formattedValue = `≅ ${this.metrics.rw.value.toLocaleString(
          "pt-BR"
        )} ${this.metrics.rw.value === 1 ? "mês" : "meses"}`;
      }
    },
    calcChartData(history) {
      this.charts.mrr.push(...history.mrr);
      this.charts.upsell.push(...history.upsell);
      const marketing_expenses = history.marketing_expenses;
      const selling_expenses = history.selling_expenses;
      const new_clients = history.new_clients;
      for (const new_client of new_clients) {
        if (new_client.value <= 0) continue;
        const marketing_expense = this.findResponseInSameMonthlyReport(
          marketing_expenses,
          new_client.date
        );
        const selling_expense = this.findResponseInSameMonthlyReport(
          selling_expenses,
          new_client.date
        );
        if (marketing_expense || selling_expense)
          this.charts.cac.push({
            value: (marketing_expense + selling_expense) / new_client.value,
            date: new_client.date,
          });
      }
      const initial_clients = history.initial_clients[0];
      if (!initial_clients || initial_clients.value < 0) return;
      const lost_clients = history.lost_clients;
      for (const lost_client of lost_clients) {
        if (lost_client.value < 0) continue;
        const sum_new_clients = this.sumResponsesUntilPreviousMonth(
          new_clients,
          lost_client.date,
          true
        );
        const sum_lost_clients = this.sumResponsesUntilPreviousMonth(
          lost_clients,
          lost_client.date,
          true
        );
        if (initial_clients.value + sum_new_clients - sum_lost_clients > 0)
          this.charts.cr.push({
            value:
              (lost_client.value /
                (initial_clients.value + sum_new_clients - sum_lost_clients)) *
              100,
            date: lost_client.date,
          });
      }
    },
    findResponseInSameMonthlyReport(reports, date) {
      const targetDate = this.moment(date, "YYYY-MM-DD");
      for (const report of reports) {
        const date = this.moment(report.date, "YYYY-MM-DD");
        if (date.isSame(targetDate, "month")) return report.value;
      }
      return null;
    },
    sumResponsesUntilPreviousMonth(reports, date, onlyPositve = false) {
      const targetDate = this.moment(date, "YYYY-MM-DD");
      let sum = 0;
      for (const report of reports) {
        if (onlyPositve && report.value <= 0) continue;
        const date = this.moment(report.date, "YYYY-MM-DD");
        if (date.isBefore(targetDate, "day")) sum += report.value;
      }
      return sum;
    },
    getPercentageStr(value) {
      return `${(Math.round(value * 100) / 100).toLocaleString("pt-BR")}%`;
    },
    handleOpenModalInfo(metric) {
      this.modals.metric = metric;
      const chartData = this.charts[metric.short_name.toLowerCase()];
      this.modals.chartData = chartData || false;
      this.modals.show = true;
    },
  },
  computed: {
    getDate() {
      return this.moment().locale("pt-BR").format("MMMM, YYYY");
    },
    notHiddenMetrics() {
      const validMetrics = {};
      for (const key of Object.keys(this.metrics)) {
        if (!this.metrics[key].hidden) validMetrics[key] = this.metrics[key];
      }
      return validMetrics;
    },
  },
};
</script>
<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
